if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

      // Perf: thumbnails beyond the first 8 are rendered with their <img>
      // stored in a <template> (see product-media-gallery.liquid) so the
      // browser never fetches them. Reveal the real <img> elements the
      // first time the thumbnail strip is scrolled or navigated — this
      // covers manual drag/swipe, the prev/next slider buttons (which call
      // slider.scrollTo under the hood) and keyboard arrow navigation
      // (which also scrolls the strip via setActiveThumbnail), since all
      // of those trigger a native 'scroll' event on the underlying list.
      this.thumbnailsRevealed = false;
      const thumbnailSlider = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
      if (thumbnailSlider && thumbnailSlider.querySelector('button template')) {
        thumbnailSlider.addEventListener('scroll', this.revealLazyThumbnails.bind(this), { once: true });
      }

      this.isHovering = false;
      this.addEventListener('mouseenter', () => { this.isHovering = true; });
      this.addEventListener('mouseleave', () => { this.isHovering = false; });
      this.boundOnKeyDown = this.onGalleryKeyDown.bind(this);
      document.addEventListener('keydown', this.boundOnKeyDown);
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    onGalleryKeyDown(event) {
      if (!this.isHovering) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const activeTag = document.activeElement ? document.activeElement.tagName : '';
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      if (!this.elements.thumbnails) return;

      const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      if (!thumbnailItems.length) return;

      const currentIndex = thumbnailItems.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
      let newIndex = currentIndex;
      if (event.key === 'ArrowLeft') newIndex = currentIndex - 1;
      if (event.key === 'ArrowRight') newIndex = currentIndex + 1;
      if (newIndex < 0 || newIndex >= thumbnailItems.length || newIndex === currentIndex) return;

      event.preventDefault();
      thumbnailItems[newIndex].querySelector('button').click();
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    // Clones the real <img> out of each lazy thumbnail's <template> and
    // appends it into the thumbnail <button>, then removes the <template>.
    // Mirrors the pattern used by ProductModal.loadLazyMedia() in
    // product-modal.js for the zoom modal's full-size images.
    revealLazyThumbnails() {
      if (this.thumbnailsRevealed || !this.elements.thumbnails) return;
      this.thumbnailsRevealed = true;
      this.elements.thumbnails.querySelectorAll('button template').forEach((lazyTemplate) => {
        const button = lazyTemplate.parentElement;
        button.appendChild(lazyTemplate.content.cloneNode(true));
        lazyTemplate.remove();
      });
    }
  });
}
